import React from "react";
import { TemplateBundlingThumbnailLoader } from "../../../components/loaders/BrowsableBundlingLoaders";
import type { Tweaks } from "./types";

export type TemplatePublicLoadingVariant = "public" | "embedded" | "embedded-preview";

export interface TemplatePublicLoadingProps {
  /** Public handle (with or without a leading @). */
  username?: string;
  /**
   * `public` — at least full viewport (e.g. `/website/:username`).
   * `embedded` — fill parent height (dashboard Website tab).
   * `embedded-preview` — preview pane under editor chrome (centered @handle).
   */
  variant?: TemplatePublicLoadingVariant;
  /** When false, only the animated rule shows (handle shown in BrowserChrome). */
  showHandle?: boolean;
  /** Active template preset / palette / fonts for loader chrome. */
  tweaks?: Partial<Tweaks> | null;
  device?: "desktop" | "tablet" | "mobile";
  /** False until saved `template_settings` are loaded (avoids default-preset flash). */
  settingsThemeReady?: boolean;
}

/**
 * Initial load for the master template — `@username` + rule; themed once settings known.
 */
export function TemplatePublicLoading({
  username,
  variant = "public",
  showHandle = true,
  tweaks,
  device = "desktop",
  settingsThemeReady = true,
}: TemplatePublicLoadingProps) {
  return (
    <TemplateBundlingThumbnailLoader
      variant={variant}
      username={username}
      showHandle={showHandle}
      tweaks={tweaks}
      device={device}
      settingsThemeReady={settingsThemeReady}
    />
  );
}

/**
 * Suspense fallback for public website — blank until the page owns loading.
 */
export function WebsiteTemplateSuspenseFallback({
  username: _usernameProp,
}: {
  username?: string;
} = {}) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading"
      style={{
        minHeight: "100dvh",
        // Inherit website palette — never flash raw white before theme paint.
        background: "var(--tpl-bg)",
      }}
    />
  );
}
